import { useLocation } from "react-router-dom";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { articleContents } from "@/mocks/articleContent";
import { categories } from "@/mocks/categories";

const siteUrl = import.meta.env.VITE_SITE_URL || "https://cssbuyspreadsheet.pro";

function stripHtml(html: string): string {
  return html.replace(/<[^>]*>/g, "").replace(/&[^;]+;/g, "").trim();
}

function truncate(text: string, max: number): string {
  if (text.length <= max) return text;
  return text.slice(0, max).replace(/\s+\S*$/, "") + "...";
}

const defaultAuthorImgUrl = "https://readdy.ai/api/search-image?query=Professional%20team%20badge%20emblem%20minimalist%20style%2C%20clean%20geometric%20circular%20design%2C%20warm%20neutral%20sand%20tones%2C%20modern%20corporate%20identity%20mark%2C%20simple%20elegant%20graphic%20symbol%2C%20cream%20background&width=200&height=200&seq=author-avatar-k3l4&orientation=squarish";

export default function ArticlePage() {
  const location = useLocation();
  const slug = location.pathname;
  
  const article = articleContents.find((a) => a.slug === slug);
  const isDefault = !article;
  const data = article || articleContents[0];

  const relatedArticles = articleContents.filter((a) => a.slug !== slug);
  const otherArticles = isDefault
    ? articleContents.slice(1, 9)
    : relatedArticles.slice(0, 8);

  const seoTitle = `${data.title} — CSSBuy Spreadsheet`;
  const seoDescription = truncate(stripHtml(data.intro), 160);
  const seoKeywords = `cssbuy spreadsheet, cssbuy sellers, ${data.category.toLowerCase()}, cssbuy guia, planilha cssbuy`;
  const articleUrl = `${siteUrl}${slug}`;

  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": data.h1,
    "description": seoDescription,
    "image": data.imageUrl,
    "datePublished": "2026-06-17",
    "dateModified": "2026-06-17",
    "author": {
      "@type": "Organization",
      "name": "CSSBuy Spreadsheet Team",
    },
    "publisher": {
      "@type": "Organization",
      "name": "CSSBuy Spreadsheet",
      "url": siteUrl,
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": articleUrl,
    },
    "inLanguage": "pt-PT",
  };

  const faqJsonLd = data.faq && data.faq.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": data.faq.map((item) => ({
      "@type": "Question",
      "name": item.q,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": stripHtml(item.a),
      },
    })),
  } : null;

  return (
    <div className="min-h-screen bg-background-50">
      <title>{seoTitle}</title>
      <meta name="description" content={seoDescription} />
      <meta name="keywords" content={seoKeywords} />
      <meta name="author" content="CSSBuy Spreadsheet Team" />
      <meta name="robots" content="index, follow" />
      <link rel="canonical" href={articleUrl} />
      <meta property="og:type" content="article" />
      <meta property="og:locale" content="pt_PT" />
      <meta property="og:site_name" content="CSSBuy Spreadsheet" />
      <meta property="og:title" content={seoTitle} />
      <meta property="og:description" content={seoDescription} />
      <meta property="og:url" content={articleUrl} />
      <meta property="og:image" content={data.imageUrl} />
      <meta property="og:image:width" content="1400" />
      <meta property="og:image:height" content="600" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={seoTitle} />
      <meta name="twitter:description" content={seoDescription} />
      <meta name="twitter:image" content={data.imageUrl} />
      <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>
      {faqJsonLd && (
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
      )}

      <Navbar />

      {/* Article Header */}
      <section className="pt-24 pb-12 bg-background-100">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <span className="inline-block bg-background-200/70 text-foreground-600 text-xs px-4 py-1.5 rounded-full mb-4">
            {data.category}
          </span>
          <h1 className="text-3xl md:text-5xl font-bold text-foreground-950 leading-tight mb-6">
            {data.h1}
          </h1>
          <div className="flex items-center gap-4 mb-8">
            <div className="w-10 h-10 rounded-full overflow-hidden bg-background-200/70">
              <img src={defaultAuthorImgUrl} alt="CSSBuy Spreadsheet Team" className="w-full h-full object-cover" />
            </div>
            <div>
              <p className="text-sm font-semibold text-foreground-950">CSSBuy Spreadsheet Team</p>
              <p className="text-xs text-foreground-500">Especialistas em Compras Internacionais • Atualizado {data.date}</p>
            </div>
          </div>

          <div className="aspect-[21/9] rounded-2xl overflow-hidden bg-background-200/70 mb-8">
            <img
              src={data.imageUrl || "https://readdy.ai/api/search-image?query=Modern%20minimalist%20workspace%20with%20laptop%20showing%20spreadsheet%20interface%20and%20organized%20product%20data%20charts%2C%20warm%20cream%20background%2C%20soft%20natural%20lighting%20from%20large%20window%2C%20clean%20wooden%20desk%20setup%2C%20editorial%20photography%2C%20professional%20yet%20inviting%20atmosphere&width=1400&height=600&seq=article-detail-h1j2&orientation=landscape"}
              alt={data.title}
              title={data.title}
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </section>

      {/* Article Content */}
      <article className="py-12 md:py-16">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="flex flex-col lg:flex-row gap-12">
            {/* Main Content */}
            <div className="flex-1 min-w-0">
              <div className="prose-custom">
                <p className="text-lg text-foreground-700 leading-relaxed mb-8" dangerouslySetInnerHTML={{ __html: data.intro }} />

                {/* Dynamic Sections */}
                {data.sections.map((section, idx) => (
                  <section key={idx}>
                    <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                      {section.h2}
                    </h2>
                    {section.paragraphs.map((p, pIdx) => (
                      <p
                        key={pIdx}
                        className="text-foreground-700 leading-relaxed mb-4"
                        dangerouslySetInnerHTML={{ __html: p }}
                      />
                    ))}
                    {section.list && (
                      <div className={section.isOrdered ? "mb-6 space-y-4 list-decimal list-inside text-foreground-700" : "mb-6 space-y-4 list-disc list-inside text-foreground-700"}>
                        {section.list.map((item, lIdx) => (
                          <p key={lIdx} className="leading-relaxed" dangerouslySetInnerHTML={{ __html: item }} />
                        ))}
                      </div>
                    )}
                  </section>
                ))}

                {/* Table */}
                {data.tableRows && (
                  <section>
                    <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                      Tabela de Referência
                    </h2>
                    <div className="overflow-x-auto mb-8 rounded-xl border border-background-200/60">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-background-100">
                            <th className="text-left px-5 py-3 text-foreground-950 font-semibold whitespace-nowrap">Categoria</th>
                            <th className="text-left px-5 py-3 text-foreground-950 font-semibold whitespace-nowrap">Preço</th>
                            <th className="text-left px-5 py-3 text-foreground-950 font-semibold whitespace-nowrap">Confiança</th>
                            <th className="text-left px-5 py-3 text-foreground-950 font-semibold whitespace-nowrap">Envio</th>
                            <th className="text-left px-5 py-3 text-foreground-950 font-semibold whitespace-nowrap">Nota</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-background-200/60">
                          {data.tableRows.map((row, i) => (
                            <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-background-50"}>
                              <td className="px-5 py-3 text-foreground-800 font-medium whitespace-nowrap">{row.cat}</td>
                              <td className="px-5 py-3 text-foreground-600 whitespace-nowrap">{row.price}</td>
                              <td className="px-5 py-3 text-accent-500 whitespace-nowrap">{row.trust}</td>
                              <td className="px-5 py-3 text-foreground-600 whitespace-nowrap">{row.ship}</td>
                              <td className="px-5 py-3">
                                <span className="bg-accent-100 text-accent-900 text-xs px-2 py-1 rounded-full whitespace-nowrap">{row.rec}</span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </section>
                )}

                {/* FAQ */}
                <section>
                  <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-6">
                    Perguntas Frequentes
                  </h2>
                  <div className="space-y-4 mb-8">
                    {data.faq.map((item, idx) => (
                      <div key={idx} className="bg-background-100 rounded-xl p-5">
                        <h3 className="text-base font-semibold text-foreground-950 mb-2">
                          {item.q}
                        </h3>
                        <p className="text-sm text-foreground-600 leading-relaxed" dangerouslySetInnerHTML={{ __html: item.a }} />
                      </div>
                    ))}
                  </div>
                </section>

                {/* Internal Links */}
                <div className="bg-background-100 rounded-2xl p-6 mb-8">
                  <p className="text-sm text-foreground-500 mb-3">
                    Continue a sua jornada de conhecimento sobre CSSBuy:
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <Link
                      to="/"
                      className="text-sm text-primary-500 hover:text-primary-600 underline whitespace-nowrap"
                    >
                      ← Voltar ao Início
                    </Link>
                    {otherArticles.map((article) => (
                      <Link
                        key={article.slug}
                        to={article.slug}
                        className="text-sm text-primary-500 hover:text-primary-600 underline whitespace-nowrap"
                      >
                        {article.title}
                      </Link>
                    ))}
                  </div>
                </div>

                {/* CTA */}
                <div className="text-center bg-background-100 rounded-3xl p-10">
                  <h3 className="text-xl font-bold text-foreground-950 mb-3">
                    Pronto para encontrar os melhores sellers?
                  </h3>
                  <p className="text-foreground-600 text-sm mb-6">
                    {data.cta}
                  </p>
                  <a
                    href="https://kaspreadsheet.com/"
                    target="_blank"
                    rel="noopener nofollow"
                    className="inline-flex items-center gap-2 bg-primary-500 text-white px-8 py-3 rounded-full text-sm font-medium hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
                  >
                    Buy It Now
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-arrow-right-line"></i>
                    </span>
                  </a>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <aside className="lg:w-80 flex-shrink-0">
              <div className="sticky top-24">
                <div className="bg-background-100 rounded-2xl p-6 mb-6">
                  <h4 className="text-sm font-semibold text-foreground-950 mb-4">
                    Artigos Relacionados
                  </h4>
                  <div className="flex flex-col gap-3">
                    {otherArticles.slice(0, 6).map((article) => (
                      <Link
                        key={article.slug}
                        to={article.slug}
                        className="text-sm text-foreground-700 hover:text-primary-500 transition-colors duration-200 underline leading-relaxed"
                      >
                        {article.title}
                      </Link>
                    ))}
                    <Link
                      to="/"
                      className="text-sm text-foreground-700 hover:text-primary-500 transition-colors duration-200 underline leading-relaxed"
                    >
                      Voltar ao Início — CSSBuy Spreadsheet
                    </Link>
                  </div>
                </div>

                <div className="bg-background-100 rounded-2xl p-6 mb-6">
                  <h4 className="text-sm font-semibold text-foreground-950 mb-4">
                    Categorias Populares
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {categories.slice(0, 10).map((cat) => (
                      <a
                        key={cat.id}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="text-xs bg-white text-foreground-600 px-3 py-1.5 rounded-full hover:bg-background-200/70 hover:text-foreground-950 transition-all duration-200 whitespace-nowrap cursor-pointer"
                      >
                        {cat.name}
                      </a>
                    ))}
                  </div>
                </div>

                <div className="bg-background-100 rounded-2xl p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full overflow-hidden bg-background-200/70 flex-shrink-0">
                      <img src={defaultAuthorImgUrl} alt="CSSBuy Spreadsheet Team" className="w-full h-full object-cover" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground-950">CSSBuy Spreadsheet Team</p>
                      <p className="text-xs text-foreground-500">Especialistas em Compras</p>
                    </div>
                  </div>
                  <p className="text-xs text-foreground-500 leading-relaxed">
                    Mantemos as CSSBuy Spreadsheets mais atualizadas da comunidade. A nossa missão é ajudar compradores a encontrar os melhores sellers com segurança e confiança.
                  </p>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
}